import { useState, useEffect, useRef } from "react";
import { getTotpCode, saveTotpSecret, deleteTotpSecret } from "../../services/credentialService.js";

/**
 * Widget 2FA TOTP — affiché dans la fiche d'une credential.
 *
 * Si un secret est configuré :
 *  → Affiche le code à 6 chiffres + barre de progression + bouton copier
 *  → Se renouvelle automatiquement toutes les 30 secondes
 *
 * Si aucun secret n'est configuré :
 *  → Affiche un formulaire pour entrer le secret TOTP
 *
 * Props :
 *  - credentialId : UUID de la credential
 *  - hasTotpSecret : boolean — true si un secret est déjà enregistré
 *  - onUpdate : callback après activation ou désactivation
 */
export default function TotpWidget({ credentialId, hasTotpSecret, onUpdate }) {

  // ── Code actuel ──────────────────────────────
  const [code,      setCode]      = useState(null);
  const [remaining, setRemaining] = useState(30);
  const [loading,   setLoading]   = useState(false);
  const [copied,    setCopied]    = useState(false);

  // ── Formulaire d'activation ──────────────────
  const [showSetup, setShowSetup] = useState(false);
  const [secret,    setSecret]    = useState("");
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");

  const intervalRef = useRef(null);

  // Charge et rafraîchit le code toutes les secondes
  useEffect(() => {
    if (!hasTotpSecret) return;

    const refresh = async () => {
      try {
        const data = await getTotpCode(credentialId);
        setCode(data.code);
        setRemaining(data.secondsRemaining);
      } catch {
        // silencieux — réessaiera à la prochaine seconde
      }
    };

    // Chargement immédiat
    setLoading(true);
    refresh().finally(() => setLoading(false));

    // Mise à jour chaque seconde
    intervalRef.current = setInterval(refresh, 1000);

    return () => clearInterval(intervalRef.current);
  }, [credentialId, hasTotpSecret]);

  const handleCopy = () => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveSecret = async () => {
    setError("");
    if (!secret.trim()) return setError("Le secret est obligatoire.");

    setSaving(true);
    try {
      await saveTotpSecret(credentialId, secret.trim());
      setSecret("");
      setShowSetup(false);
      onUpdate?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Désactiver le 2FA pour cette credential ?")) return;
    try {
      await deleteTotpSecret(credentialId);
      setCode(null);
      clearInterval(intervalRef.current);
      onUpdate?.();
    } catch (err) {
      alert(err.message);
    }
  };

  // Formate le code en deux groupes de 3 : "483 217"
  const fmtCode = (c) => c ? `${c.slice(0, 3)} ${c.slice(3)}` : "— —";

  // Pourcentage de la barre (pleine au début, vide à la fin)
  const pct = (remaining / 30) * 100;

  // Couleur selon l'urgence
  const barColor = remaining > 10 ? "#00C2CB" : remaining > 5 ? "#F59E0B" : "#EF4444";

  // ── Cas : secret configuré → affiche le code ─
  if (hasTotpSecret) {
    return (
      <div style={{ background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.1)", borderRadius:10, padding:"12px 14px" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:600, color:"var(--text-2)" }}>
            <i className="bi bi-shield-lock-fill" style={{ color:"#00C2CB" }}/>
            Code 2FA
          </div>
          <button
            onClick={handleDelete}
            style={{ fontSize:11, background:"none", border:"none", cursor:"pointer", color:"var(--danger)", opacity:.7 }}
            title="Désactiver le 2FA"
          >
            <i className="bi bi-x-circle me-1"/>Désactiver
          </button>
        </div>

        {/* Code affiché */}
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ fontFamily:"monospace", fontSize:28, fontWeight:700, color:"var(--text-1)", letterSpacing:".15em", flex:1 }}>
            {loading ? "— —" : fmtCode(code)}
          </div>
          <button
            onClick={handleCopy}
            disabled={!code}
            style={{ background:"rgba(0,194,203,.15)", border:"1px solid rgba(0,194,203,.3)", borderRadius:8, padding:"6px 12px", color:"#00C2CB", fontSize:12, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", gap:6, transition:"all .15s" }}
          >
            <i className={`bi ${copied ? "bi-check2" : "bi-copy"}`}/>
            {copied ? "Copié !" : "Copier"}
          </button>
        </div>

        {/* Barre de progression */}
        <div style={{ marginTop:8 }}>
          <div style={{ height:4, background:"rgba(255,255,255,.08)", borderRadius:99, overflow:"hidden" }}>
            <div style={{
              height:"100%", borderRadius:99,
              width:`${pct}%`,
              background: barColor,
              transition:"width 1s linear, background .5s",
              boxShadow:`0 0 6px ${barColor}88`,
            }}/>
          </div>
          <div style={{ display:"flex", justifyContent:"flex-end", marginTop:4, fontSize:11, color:"var(--text-3)" }}>
            Renouvellement dans {remaining}s
          </div>
        </div>
      </div>
    );
  }

  // ── Cas : pas de secret → formulaire d'activation ─
  return (
    <div style={{ background:"rgba(255,255,255,.03)", border:"1px dashed rgba(255,255,255,.12)", borderRadius:10, padding:"12px 14px" }}>
      {!showSetup ? (
        <button
          onClick={() => setShowSetup(true)}
          style={{ width:"100%", background:"none", border:"none", cursor:"pointer", color:"var(--text-3)", fontSize:13, display:"flex", alignItems:"center", gap:8, padding:0 }}
        >
          <i className="bi bi-shield-plus" style={{ fontSize:16 }}/>
          <div style={{ textAlign:"left" }}>
            <div style={{ fontWeight:600, color:"var(--text-2)" }}>Activer le 2FA</div>
            <div style={{ fontSize:11, marginTop:2 }}>Générer les codes TOTP pour cette application</div>
          </div>
        </button>
      ) : (
        <div>
          <div style={{ fontSize:12, fontWeight:600, color:"var(--text-2)", marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
            <i className="bi bi-shield-lock-fill" style={{ color:"#00C2CB" }}/>
            Configurer le 2FA
          </div>

          {/* Instructions */}
          <div style={{ fontSize:12, color:"var(--text-3)", marginBottom:10, lineHeight:1.6 }}>
            <strong style={{ color:"var(--text-2)" }}>Comment obtenir le secret ?</strong>
            <ol style={{ margin:"6px 0 0 16px", padding:0 }}>
              <li>Activez le 2FA dans l'application tierce (GitHub, Google…)</li>
              <li>Choisissez "Configurer une app d'authentification"</li>
              <li>Cherchez le lien "Entrer le code manuellement" pour voir le secret</li>
              <li>Copiez-le ici</li>
            </ol>
          </div>

          {error && (
            <div style={{ fontSize:12, color:"var(--danger)", background:"var(--danger-dim)", borderRadius:7, padding:"7px 10px", marginBottom:8 }}>
              {error}
            </div>
          )}

          {/* Champ secret */}
          <div style={{ display:"flex", gap:6 }}>
            <input
              style={{ flex:1, background:"rgba(255,255,255,.06)", border:"1px solid rgba(255,255,255,.15)", borderRadius:7, color:"var(--text-1)", padding:"8px 10px", fontSize:13, fontFamily:"monospace", outline:"none", letterSpacing:".08em" }}
              value={secret}
              onChange={e => setSecret(e.target.value.replace(/\s/g, "").toUpperCase())}
              placeholder="JBSWY3DPEHPK3PXP"
            />
            <button
              onClick={handleSaveSecret}
              disabled={saving || !secret.trim()}
              style={{ background:"rgba(0,194,203,.15)", border:"1px solid rgba(0,194,203,.3)", borderRadius:7, color:"#00C2CB", fontSize:12, fontWeight:600, padding:"8px 14px", cursor:"pointer", whiteSpace:"nowrap" }}
            >
              {saving ? <i className="bi bi-arrow-repeat spin"/> : "Activer"}
            </button>
            <button
              onClick={() => { setShowSetup(false); setSecret(""); setError(""); }}
              style={{ background:"none", border:"1px solid rgba(255,255,255,.12)", borderRadius:7, color:"var(--text-3)", fontSize:12, padding:"8px 12px", cursor:"pointer" }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin .7s linear infinite; display: inline-block; }
      `}</style>
    </div>
  );
}